import { useEffect } from 'react';

interface SEOData {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogTitle?: string;
  ogDescription?: string;
  structuredData?: object;
  noindex?: boolean;
}

export const useSEO = (seoData: SEOData) => {
  useEffect(() => {
    // Update title
    document.title = seoData.title;

    // Update meta tags
    const updateMetaTag = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Update canonical link
    const updateCanonical = (url: string) => {
      let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.rel = 'canonical';
        document.head.appendChild(canonical);
      }
      canonical.href = url;
    };

    // Update structured data
    const updateStructuredData = (data: object) => {
      let script = document.querySelector('script[data-page-schema]') as HTMLScriptElement;
      if (!script) {
        script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-page-schema', 'true');
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
    };

    // Apply updates
    updateMetaTag('description', seoData.description);
    if (seoData.keywords) updateMetaTag('keywords', seoData.keywords);
    if (seoData.canonical) updateCanonical(seoData.canonical);
    
    // Open Graph
    updateMetaTag('og:title', seoData.ogTitle || seoData.title, true);
    updateMetaTag('og:description', seoData.ogDescription || seoData.description, true);
    if (seoData.canonical) updateMetaTag('og:url', seoData.canonical, true);
    
    // Twitter
    updateMetaTag('twitter:title', seoData.ogTitle || seoData.title);
    updateMetaTag('twitter:description', seoData.ogDescription || seoData.description);

    // Structured data
    if (seoData.structuredData) {
      updateStructuredData(seoData.structuredData);
    }

    // Robots: prerendered HTML carries the tag on first load; keep it in sync
    // when navigating client-side between indexable and noindex pages.
    if (seoData.noindex) {
      updateMetaTag('robots', 'noindex, follow');
    } else {
      document.querySelector('meta[name="robots"]')?.remove();
    }

    // Cleanup function
    return () => {
      if (seoData.noindex) {
        document.querySelector('meta[name="robots"]')?.remove();
      }
      // Remove page-specific structured data when component unmounts
      const pageSchema = document.querySelector('script[data-page-schema]');
      if (pageSchema) {
        pageSchema.remove();
      }
    };
  }, [seoData]);
};